import { useEffect, useMemo, useState } from "react";
import { getClasses, getStudentsForClass } from "../../services/APIClient";
import {
  getClassGradeLevel,
  normalizeAcademicYear,
} from "../EOYReportAssistant/EOYReportAssistantPanel";
import useAcademicYearOptions from "../../utils/useAcademicYearOptions";

function getClassAcademicYear(classRecord) {
  return classRecord?.academicYear || classRecord?.academic_year || "";
}

// A class represents a real classroom for the target year when it has an
// explicit academic year matching that year and isn't a trial/deleted
// record. Once a school year ends its classes get archived — `active`
// flips to false and `snapshot` to true — so those two flags only get
// checked when the target year is genuinely the current year (e.g. New
// Class Insights' destination class, which must be a live classroom).
// A teacher picking a past year on purpose (Screening Point, PTM) should
// still see that year's now-archived classes; only trial/deleted classes
// are excluded there.
function isRealReportClass(classRecord, academicYear, isCurrentYear) {
  const classAcademicYear = getClassAcademicYear(classRecord);
  if (
    !classAcademicYear ||
    normalizeAcademicYear(classAcademicYear) !== academicYear ||
    classRecord?.deleted === true ||
    classRecord?.trial_class === true ||
    classRecord?.trialClass === true
  ) {
    return false;
  }
  if (isCurrentYear) {
    return classRecord?.active !== false && classRecord?.snapshot !== true;
  }
  return true;
}

/**
 * Class/grade/student picking state shared across all Enhanced AI Report
 * tabs, so switching tabs doesn't lose the teacher's current class/student
 * selection. Generalizes the class+student loading portion of
 * EOYReportAssistantPanel.js (report generation/state is intentionally not
 * part of this hook — each tab owns its own useEnhancedReportWorkflow).
 *
 * `academicYearOverride` lets a tab (Screening Point, PTM) request classes
 * for a specific academic year instead of the current one. When it changes,
 * the class list is refetched/refiltered and any stale class/pupil
 * selection is cleared. Pass nothing to keep the previous "current year
 * only" behaviour (used by New Class Insights' destination-class picker).
 */
function useClassSelection(academicYearOverride) {
  const [currentAcademicYearOption] = useAcademicYearOptions({ previousYears: 0 });
  const currentAcademicYear = currentAcademicYearOption.storage;
  const effectiveAcademicYear = academicYearOverride || currentAcademicYear;

  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [classesError, setClassesError] = useState(null);

  const [selectedGradeLevel, setSelectedGradeLevel] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedClassName, setSelectedClassName] = useState("");
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);

  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const filteredClasses = useMemo(
    () =>
      selectedGradeLevel
        ? classes.filter(
            (classRecord) => getClassGradeLevel(classRecord) === selectedGradeLevel
          )
        : [],
    [classes, selectedGradeLevel]
  );

  useEffect(() => {
    // A previously selected class/pupils may not exist in the new year's
    // class list, so clear them whenever the effective year changes rather
    // than leaving a stale (and now year-mismatched) selection in place.
    setSelectedGradeLevel("");
    setSelectedClassId("");
    setSelectedClassName("");
    setSelectedStudentIds([]);
    setDisclaimerAccepted(false);

    let cancelled = false;

    async function loadClasses() {
      setClassesLoading(true);
      setClassesError(null);
      try {
        const data = await getClasses();
        if (cancelled) return;
        const allClasses = Array.isArray(data) ? data : data?.classes || [];
        const isCurrentYear = effectiveAcademicYear === currentAcademicYear;
        const yearClasses = allClasses.filter((classRecord) =>
          isRealReportClass(classRecord, effectiveAcademicYear, isCurrentYear)
        );
        setClasses(yearClasses);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load classes:", err);
        setClassesError("Could not load your classes. Please try refreshing.");
      } finally {
        if (!cancelled) setClassesLoading(false);
      }
    }

    loadClasses();
    return () => {
      cancelled = true;
    };
  }, [effectiveAcademicYear, currentAcademicYear]);

  useEffect(() => {
    if (!selectedClassId) {
      setStudents([]);
      setSelectedStudentIds([]);
      setStudentsLoading(false);
      return;
    }

    let cancelled = false;

    async function loadStudents() {
      setStudentsLoading(true);
      setStudentsError(null);
      setSelectedStudentIds([]);

      try {
        const studentsData = await getStudentsForClass(selectedClassId);
        if (cancelled) return;
        const list = studentsData?.students || studentsData || [];
        setStudents(Array.isArray(list) ? list : []);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load students:", err);
        setStudentsError("Could not load students for this class.");
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    }

    loadStudents();
    return () => {
      cancelled = true;
    };
  }, [selectedClassId]);

  const handleGradeLevelChange = (gradeLevel) => {
    setSelectedGradeLevel(gradeLevel);
    setSelectedClassId("");
    setSelectedClassName("");
    setDisclaimerAccepted(false);
  };

  const handleClassChange = (classId) => {
    setSelectedClassId(classId);
    const classRecord = classes.find((c) => String(c.id) === String(classId));
    setSelectedClassName(classRecord?.name || "");
    setDisclaimerAccepted(false);
  };

  const handleStudentToggle = (studentId, unavailableStudentIds = []) => {
    if (unavailableStudentIds.includes(studentId)) return;

    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = (unavailableStudentIds = []) => {
    const unavailableSet = new Set(unavailableStudentIds);
    const selectableStudentIds = students
      .map((student) => student.id)
      .filter((studentId) => !unavailableSet.has(studentId));

    setSelectedStudentIds((prev) =>
      prev.length === selectableStudentIds.length ? [] : selectableStudentIds
    );
  };

  return {
    currentAcademicYear,
    effectiveAcademicYear,
    classes,
    classesLoading,
    classesError,
    filteredClasses,
    selectedGradeLevel,
    selectedClassId,
    selectedClassName,
    students,
    studentsLoading,
    studentsError,
    selectedStudentIds,
    disclaimerAccepted,
    setDisclaimerAccepted,
    handleGradeLevelChange,
    handleClassChange,
    handleStudentToggle,
    handleSelectAll,
  };
}

export default useClassSelection;
